import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronLeft from "@store/Components/Icons/ChevronLeft";
import ChevronRight from "@store/Components/Icons/ChevronRight";
import { COLORS, PRODUCT } from "@store/theme/design";
import { imageStyles } from "@store/theme/mixins";
import { dotWrapperStyles, getDotButtonProps } from "@store/theme/props/carousel";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import Color from "color";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useState } from "react";
import { useCallback, useEffect } from "react";
import { css } from "styled-components";

interface OwnProps {
    imageCollection: ImgGalleryModel[];
    productDetailPath: string;
    imgLoading: "eager" | "lazy";
    onClick?: React.EventHandler<React.MouseEvent>;
}

export interface ImgGalleryModel {
    id: string;
    imageAltText: string;
    imageType: string;
    largeImagePath: string;
    mediumImagePath: string;
    name: string;
    smallImagePath: string;
    sortOrder: number;
}

export interface ProductListImgSlideshowStyles {
    wrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    arrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: ClickablePresentationProps;
    slideWrapperStyles?: InjectableCss;
    img?: ImgProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

const arrowWrapperStyles = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        background-color: ${Color(COLORS.primary.base).rgb().alpha(0.6).string()};
        opacity: 0;
        transition: opacity .5s ease;

        ${({ theme }) =>
            breakpointValuesMediaQueries(
                theme,
                [{ values: ["none", "block"], properties: ["display"] }],
                mediaOptions.min,
            )}
    `,
};

export const productListImgSlideshowStyles: ProductListImgSlideshowStyles = {
    wrapper: {
        css: css`
            position: relative;
        `,
    },
    prevArrowWrapper: {
        ...arrowWrapperStyles,
    },
    nextArrowWrapper: {
        css: css`
            ${arrowWrapperStyles?.css}
            right: 0;
        `,
    },
    arrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
                border: 1px solid ${COLORS.outline};
            }
        `,
    },
    iconProps: {},
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideWrapperStyles: {
        css: css`
            ${imageStyles(PRODUCT.image.ratio)}
            display: flex;
            flex: 0 0 100%;
            position: relative;
            width: 100%;
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            ${dotWrapperStyles()}
            position: absolute;
            transform: translateY(70%);
            transition: opacity .5s ease;

            ${({ theme }) =>
            breakpointValuesMediaQueries(
                theme,
                [{ values: ["1", "0.5"], properties: ["opacity"] }],
                mediaOptions.min,
            )}
        `,
    },
    dotButton: getDotButtonProps(false, COLORS.secondary.base),
    dotButtonSelected: getDotButtonProps(true, COLORS.primary.base),
}

const styles = productListImgSlideshowStyles;

const ProductListImgSlideshow = ({ imageCollection, productDetailPath, imgLoading, onClick }: OwnProps) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [slideIndex, setSlideIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSlideIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
            startIndex: slideIndex,
        });
    }, [embla, imageCollection]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.prevArrowWrapper} className="carousel-arrow">
                <Button {...styles.arrowButton} onClick={() => embla && embla.scrollPrev()}>
                    <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <Clickable
                    {...styles.slideContainerWrapper}
                    href={productDetailPath}
                    data-test-selector="productImage"
                    onClick={onClick}
                >
                    {imageCollection.map(({ mediumImagePath, name, imageAltText }) => {
                        return (
                            <StyledWrapper {...styles.slideWrapperStyles} key={`${name}`}>
                                <Img
                                    {...styles.img}
                                    loading={imgLoading}
                                    src={mediumImagePath}
                                    altText={imageAltText}
                                />
                            </StyledWrapper>
                        );
                    })}
                </Clickable>
            </StyledWrapper>
            <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                {imageCollection.map((_, i) => {
                    return (
                        <Button
                            className="slide-dot-button"
                            // eslint-disable-next-line react/no-array-index-key
                            key={`dot-${Math.floor(Math.random() * 10000)}`}
                            {...(i === slideIndex ? styles.dotButtonSelected : styles.dotButton)}
                            onClick={() => embla && embla.scrollTo(i)}
                        />
                    );
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.nextArrowWrapper} className="carousel-arrow">
                <Button {...styles.arrowButton} onClick={() => embla && scrollNext()}>
                    <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductListImgSlideshow;
